import React from 'react'
import { UpsellModal } from "components/modals/UpsellModal"
import { showModal } from "components/utils/CustomModal"
import { GetCurrentSubscriptionRes } from "graphql/queries/user/getUser"
import intl from "react-intl-universal"
import { track } from "utils/track"
import { getAiCreditsState } from "./aiCredits"

export const checkAndShowAiCreditsModal = (credits, userData: GetCurrentSubscriptionRes|undefined, organizationId, sendBillingAdminLockedFeatureEmail, onAiCreditsRequested?: () => void) => {
  const isBillingAdmin = userData?.user.billingManagerOrganizations.includes(organizationId) || false

  // Per-creator credits outrank the billing-admin upsell, and a billing admin is
  // *not* exempt from them: a workspace can have twenty admins and only five
  // people who should create with AI, so an admin with no allocation is a normal
  // state — not an empty workspace pool. Sending them to "buy more AI Tasks"
  // would be wrong; the workspace may have plenty left.
  const creatorCreditsEnforced = !!credits?.creatorCreditsEnforced

  // Via `getAiCreditsState` rather than reading `creator.isAssigned` directly, so
  // this gate, the footer display and the note cannot disagree — which they did:
  // an exhausted *workspace pool* was reported as a creator shortfall, sending an
  // admin off to assign AI Tasks the workspace does not have.
  const creditsState = getAiCreditsState(credits)

  const modalType = creditsState === 'workspaceExhausted' || !creatorCreditsEnforced
    ? (isBillingAdmin
      ? 'ai_credits_limit_reached_billing_admin' as "ai_credits_limit_reached_billing_admin"
      : 'feature_locked_ai_credits_user_notify_admin' as "feature_locked_ai_credits_user_notify_admin")
    : creditsState === 'notAssigned'
      ? 'ai_credits_creator_none_assigned' as "ai_credits_creator_none_assigned"
      : 'ai_credits_creator_insufficient' as "ai_credits_creator_insufficient"

  if (credits.totalCredits > credits.availableCredits) {
    const modal = showModal({
        title: intl.get('home_group_invite'),
        component: <UpsellModal
          type={modalType}
          workspaceId={organizationId}
          track={modalType}
          creditsNeeded={credits?.totalCredits || 0}
          creditsAvailable={credits?.creator?.availableCredits ?? credits?.availableCredits ?? 0}
          creditsAssigned={credits?.creator?.assignedCredits ?? 0}
          onAiCreditsRequested={onAiCreditsRequested}
          close={() => {
            modal.close()
          }} sendEmail={sendBillingAdminLockedFeatureEmail} />,
        onlyContent: true,
        className: 'add-action-modal',
      })

      // Skipped entirely when credits are metered per creator: the admin is
      // already told by the backend's automatic depletion email, or by the
      // creator pressing "Request AI Tasks". A locked-feature email on top would
      // be noise, and it would be about the wrong thing.
      if (isBillingAdmin && (!creatorCreditsEnforced || creditsState === 'workspaceExhausted')) {
        sendBillingAdminLockedFeatureEmail({
          variables: {
            feature: 'feature-locked-ai-credits-upsell-modal',
            organizationId: organizationId
            },
        })
        track({
          event: 'Email Sent: Ai Credits Limit Reached - Is Billing Admin',
          variables: {
            organizationId,
          }
        })
      }

    return true
  }
  return false
}
